'use client';
import { useLayoutEffect, useRef } from 'react';

import type { AnimatedNumberProps } from '~shared/ui/atoms';
import { AnimatedNumber } from '~shared/ui/atoms';

import { AnimatedContent } from './styles';

type Props = {
	contentPulseInterval?: number;
	children: React.ReactNode;
} & AnimatedNumberProps;

export const AnimatedNumberWithContent = ({
	children,
	value,
	contentPulseInterval = 100,
	duration = [{ delta: 0, duration: 500 }],
	animationType = 'both',
	className,
}: Props) => {
	const animatedContentRef = useRef<HTMLDivElement>(null);
	const prevValue = useRef(value);

	const sortedDuration = duration.sort((a, b) => b.delta - a.delta);

	useLayoutEffect(() => {
		if (!animatedContentRef.current) {
			return;
		}
		if (value !== prevValue.current) {
			const difference = value - prevValue.current;
			const delta = Math.abs(difference);
			const elemDuration = sortedDuration.find((dur) => dur.delta < delta);

			let currentDuration = elemDuration ? elemDuration.duration : 5;
			if (
				(animationType === 'increase' && difference < 0) ||
				(animationType === 'decrease' && difference > 0)
			) {
				currentDuration = contentPulseInterval;
			}
			animatedContentRef.current.style.animationName = 'none';

			requestAnimationFrame(() => {
				if (animatedContentRef.current) {
					animatedContentRef.current.style.setProperty(
						'--anim-repeat',
						(currentDuration / contentPulseInterval).toString()
					);
					animatedContentRef.current.style.setProperty(
						'--anim-duration',
						`${contentPulseInterval}ms`
					);
				}
				requestAnimationFrame(() => {
					if (animatedContentRef.current) {
						animatedContentRef.current.style.animationName = '';
					}
				});
			});

			prevValue.current = value;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	return (
		<span className={className}>
			<AnimatedNumber
				value={value}
				animationType={animationType}
				duration={duration}
			/>
			<AnimatedContent ref={animatedContentRef}>{children}</AnimatedContent>
		</span>
	);
};
